/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WASP Dashboard v1.0.2 - Tab de Configuración (Componente Principal)
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file ConfigurationTab.tsx
 * @description Tab principal de configuración con secciones modulares
 * @version 1.0.2
 * @date 2024-11-02
 * @architecture Frontend → Proxy (backend.anubisai.net) → WASP (192.168.112.3)
 * 
 * ESTRUCTURA:
 * ├─ Control del Sistema (switches principales)
 * ├─ Gestión de Grupos (GroupsManagementSection)
 * ├─ Multimedia y Almacenamiento (MultimediaSection)
 * ├─ Endpoints de IA (AIEndpointsSection)
 * ├─ Ubicaciones en Tiempo Real (LocationsSection)
 * ├─ Red LinkHub [BLOQUEADO] (LinkHubSection)
 * └─ Estadísticas y Monitoreo (MonitoringSection)
 * 
 * PROPS REQUERIDOS:
 * - user: Usuario autenticado con mlStuff
 * - isWhatsAppEnabled: Estado del switch principal
 * - isDirectivesEnabled: Estado de directivas
 * - isCoPilotEnabled: Estado de CoPilot
 * - loadingToggles: { whatsapp, directives, copilot }
 * - serviceIndicators: Array de indicadores de servicios
 * - errorMessage/successMessage: Alertas
 * - setErrorMessage/setSuccessMessage: Setters de alertas
 * - handleToggleWhatsApp/Directives/CoPilot: Handlers de toggles
 * - hasWsFeatureEnabled: Función de validación de features
 * 
 * CAMBIOS EN v1.0.2:
 * - Separación del tab Config en componente independiente
 * - Modularización de secciones en archivos separados
 * - Mantiene Control del Sistema en este archivo (core)
 * - Prepara integración de 6 secciones adicionales
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React from 'react';
import { Alert, Card, Form, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleDot } from '@fortawesome/free-solid-svg-icons';

// Importar secciones modulares (se crearán después)
import GroupsManagementSection from '../components/WASP/GroupsManagementSection';
import MultimediaSection from '../components/WASP/MultimediaSection';
import AIEndpointsSection from '../components/WASP/AIEndpointsSection';
import LocationsSection from '../components/WASP/LocationsSection';
import LinkHubSection from '../components/WASP/LinkHubSection';
import MonitoringSection from '../components/WASP/MonitoringSection';

// ═══════════════════════════════════════════════════════════════════════════
// INTERFACES
// ═══════════════════════════════════════════════════════════════════════════

interface ServiceIndicator {
  name: string;
  status: 'online' | 'offline' | 'checking';
  icon: any;
  color: string;
}

interface LoadingToggles {
  whatsapp: boolean;
  directives: boolean;
  copilot: boolean;
}

interface ConfigurationTabProps {
  user: any;
  isWhatsAppEnabled: boolean;
  isDirectivesEnabled: boolean;
  isCoPilotEnabled: boolean;
  loadingToggles: LoadingToggles;
  serviceIndicators: ServiceIndicator[];
  errorMessage: string | null;
  successMessage: string | null;
  setErrorMessage: (msg: string | null) => void;
  setSuccessMessage: (msg: string | null) => void;
  handleToggleWhatsApp: () => void;
  handleToggleDirectives: () => void;
  handleToggleCoPilot: () => void;
  hasWsFeatureEnabled: (user: any, feature: string) => boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const ConfigurationTab: React.FC<ConfigurationTabProps> = ({
  user,
  isWhatsAppEnabled,
  isDirectivesEnabled,
  isCoPilotEnabled,
  loadingToggles,
  serviceIndicators,
  errorMessage,
  successMessage,
  setErrorMessage,
  setSuccessMessage,
  handleToggleWhatsApp,
  handleToggleDirectives,
  handleToggleCoPilot,
  hasWsFeatureEnabled
}) => {
  
  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      
      {/* ═══════════════════════════════════════════════════════════════════
          ALERTAS
          ═══════════════════════════════════════════════════════════════════ */}
      {errorMessage && (
        <Alert variant="danger" dismissible onClose={() => setErrorMessage(null)} className="mb-3">
          {errorMessage}
        </Alert>
      )}
      
      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)} className="mb-3">
          {successMessage}
        </Alert>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          INDICADORES DE SERVICIOS
          ═══════════════════════════════════════════════════════════════════ */}
      <Row className="mb-4">
        {serviceIndicators.map((indicator, index) => (
          <Col key={index} md={4} className="mb-3">
            <Card style={{ 
              border: 'none', 
              borderRadius: '12px', 
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
              backgroundColor: 'var(--bs-body-bg)' 
            }}>
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-3">
                    <div
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        backgroundColor: `${indicator.color}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem',
                        color: indicator.color
                      }}
                    >
                      <FontAwesomeIcon icon={indicator.icon} />
                    </div>
                    <div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)', marginBottom: '2px' }}>
                        {indicator.name}
                      </div>
                      <div style={{ fontSize: '1rem', fontWeight: 600, color: indicator.color }}>
                        {indicator.status === 'online' ? 'En línea' : 'Fuera de línea'}
                      </div>
                    </div>
                  </div>
                  <FontAwesomeIcon
                    icon={faCircleDot}
                    style={{ fontSize: '0.8rem', color: indicator.color }}
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* ═══════════════════════════════════════════════════════════════════
          SECCIÓN 1: CONTROL DEL SISTEMA (CORE)
          ═══════════════════════════════════════════════════════════════════ */}
      <Card style={{ 
        border: 'none', 
        borderRadius: '12px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
        backgroundColor: 'var(--bs-body-bg)',
        marginBottom: '24px'
      }}>
        <Card.Header style={{ 
          backgroundColor: 'var(--bs-body-bg)', 
          borderBottom: '1px solid var(--bs-border-color)', 
          padding: '16px 20px' 
        }}>
          <h5 className="mb-0" style={{ fontWeight: 600 }}>Control del Sistema</h5>
        </Card.Header>
        <Card.Body style={{ padding: '24px' }}>
          
          {/* Switch 1: WhatsApp Assistant */}
          <div className="mb-4 pb-4" style={{ borderBottom: '1px solid var(--bs-border-color)' }}>
            <div className="d-flex align-items-center justify-content-between">
              <div style={{ flex: 1 }}>
                <h6 style={{ fontWeight: 600, marginBottom: '8px' }}>
                  WhatsApp Assistant
                </h6>
                <p style={{ fontSize: '0.9rem', color: 'var(--bs-secondary)', marginBottom: 0 }}>
                  Activa el bot para responder mensajes y procesar comandos básicos
                </p>
              </div>
              <Form.Check
                type="switch"
                id="switch-whatsapp"
                checked={isWhatsAppEnabled}
                onChange={handleToggleWhatsApp}
                disabled={loadingToggles.whatsapp || !hasWsFeatureEnabled(user, 'whatsapp-assistant')}
                style={{ fontSize: '1.5rem' }}
              />
            </div>
          </div>

          {/* Switch 2: Directivas */}
          <div className="mb-4 pb-4" style={{ borderBottom: '1px solid var(--bs-border-color)' }}>
            <div className="d-flex align-items-center justify-content-between">
              <div style={{ flex: 1 }}>
                <h6 style={{ fontWeight: 600, marginBottom: '8px' }}>
                  Directivas de Respuesta Automática
                </h6>
                <p style={{ fontSize: '0.9rem', color: 'var(--bs-secondary)', marginBottom: 0 }}>
                  Habilita flows dinámicos y respuestas automáticas personalizadas
                </p>
              </div>
              <Form.Check
                type="switch"
                id="switch-directives"
                checked={isDirectivesEnabled}
                onChange={handleToggleDirectives}
                disabled={!isWhatsAppEnabled || loadingToggles.directives || !hasWsFeatureEnabled(user, 'automatic-response-directives')}
                style={{ fontSize: '1.5rem' }}
              />
            </div>
          </div>

          {/* Switch 3: CoPilot */}
          <div>
            <div className="d-flex align-items-center justify-content-between">
              <div style={{ flex: 1 }}>
                <h6 style={{ fontWeight: 600, marginBottom: '8px' }}>
                  Modo CoPilot (IA Avanzada)
                </h6>
                <p style={{ fontSize: '0.9rem', color: 'var(--bs-secondary)', marginBottom: 0 }}>
                  Activa sistema de IA con fallback LHP → Anubis → Friday → ML
                </p>
              </div>
              <Form.Check
                type="switch"
                id="switch-copilot"
                checked={isCoPilotEnabled}
                onChange={handleToggleCoPilot}
                disabled={!isWhatsAppEnabled || !isDirectivesEnabled || loadingToggles.copilot || !hasWsFeatureEnabled(user, 'copilot-mode')}
                style={{ fontSize: '1.5rem' }}
              />
            </div>
          </div>

        </Card.Body>
      </Card>

      {/* ═══════════════════════════════════════════════════════════════════
          SECCIONES MODULARES (Por implementar)
          ═══════════════════════════════════════════════════════════════════ */}
      
      <GroupsManagementSection user={user} />
      <MultimediaSection user={user} />
      {/*<AIEndpointsSection locked={true} user={user} />*/}
      <LocationsSection user={user} />
      {/*<LinkHubSection locked={true} />
      <MonitoringSection locked={true} user={user} />*/}

    </div>
  );
};

export default ConfigurationTab;